import CurrencyIcon from "components/shared/CurrencyIcon";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import MessageBlock from "components/shared/MessageBlock";
import type { TModalActionButton } from "components/shared/ModalActionButtons";
import ModalActionButtons from "components/shared/ModalActionButtons";
import { createEnumFromObject } from "helpers/utils";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { DTOUpdateBalance, GeneralTabTerm } from "store/managementStore";
import { updateInitialBalance } from "store/managementStore";
import { useAppDispatch, useAppSelector } from "store/store";

import styles from "./BalanceOverride.module.scss";

interface IBalanceOverrideOption {
    term: GeneralTabTerm | null;
    handleClose: VoidFunction;
    /**
     * Triggers animation on balance cell.
     */
    setBalanceUpdateSuccessful: React.Dispatch<
        React.SetStateAction<{
            termId: number | null;
            animate: boolean;
        }>
    >;
}

interface IOverrideBalanceForm {
    amount: number;
    comment: string | null;
}

/**
 * Option on BalanceUpdateModal for overriding current opening balance.
 */
const BalanceOverrideOption = ({ term, handleClose, setBalanceUpdateSuccessful }: IBalanceOverrideOption) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { activePeriod, periodsDateRange } = useAppSelector((state) => state.period);

    const [requestPending, setRequestPending] = useState(false);

    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);

    const defaultValues = {
        amount: (term?.balancePrev ?? 0) / 100,
        comment: null,
    };

    const EFormValues = createEnumFromObject(defaultValues);

    const originalPeriodClosed = term?.originalPeriod !== activePeriod;

    const {
        control,
        formState: { errors },
        handleSubmit,
    } = useForm<IOverrideBalanceForm>({ defaultValues, mode: "all" });

    const buttonsDetails: TModalActionButton = {
        cancel: { label: t("ModalBase.Cancel"), action: () => handleClose() },
        confirm: { label: t("ModalBase.Confirm"), type: "submit" },
    };

    const onSubmit = async (formData: IOverrideBalanceForm) => {
        if (!term || !activePeriod) return;

        const data: DTOUpdateBalance = {
            ...formData,
            amount: Math.round(formData.amount * 100),
            periodId: activePeriod,
            throughAdvance: 0,
        };

        setRequestPending(true);

        const response = await dispatch(
            updateInitialBalance({
                data,
                termId: term.id,
                isOriginalPeriodClosed: originalPeriodClosed,
            }),
        );

        if (response.meta.requestStatus === "fulfilled") {
            updatePaymentPageState();
            handleClose();
            setBalanceUpdateSuccessful({ termId: term.id, animate: true });
        }

        setRequestPending(false);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
            <div>
                <NumberFormComponent
                    control={control}
                    controlName={EFormValues.amount}
                    label={t("BalanceUpdate.NewAmount")}
                    icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                    error={errors.amount}
                    float
                    required
                    acceptsNegative
                />
                <span className={styles.balanceInfo}>{t("ConnectTitle.AdvanceInfo")}</span>
            </div>

            {originalPeriodClosed && (
                <TextFormComponent
                    control={control}
                    controlName={EFormValues.comment}
                    label={t("BalanceUpdate.EditModal.AddReason")}
                    error={errors.comment}
                    required={originalPeriodClosed}
                />
            )}
            {!!term?.hasAdvances && <MessageBlock type="warning" text={t("BalanceUpdate.OverrideWarning")} />}
            <ModalActionButtons buttonsDetails={buttonsDetails} loading={requestPending} />
        </form>
    );
};

export default BalanceOverrideOption;
